import type { FunctionComponent } from 'react';
import moment from 'moment';
import Image from 'next/image';
import Box from '@material-ui/core/Box';

import { FlowType } from '@jerry-serverless/types';

import MaintenanceOilChangeIcon from '../components/icon/maintenance_oil_change_icon/MaintenanceOilChangeIcon';
import MaintenanceTireRotatedIcon from '../components/icon/maintenance_tire_rotated_icon/MaintenanceTireRotatedIcon';
import MaintenanceBrakePadIcon from '../components/icon/maintenance_brake_pad_icon/MaintenanceBrakePadIcon';
import type { EventName } from '../utils/analytics';
import CalenderImage from '../components/garage_only/assets/calender.png';
import OilBarrelImage from '../components/garage_only/assets/oilbarrel.png';

export enum AddMaintenanceReminderSteps {
  OilChange = 'MaintenanceOilChange',
  OilType = 'MaintenanceOilType',
  TireRotated = 'MaintenanceTireRotated',
  BrakePad = 'MaintenanceBrakePad',
}

export enum UpdateDate {
  HalfMonth = 0.5 * 30,
  TwoMonths = 2 * 30,
  ThreeMonths = 3 * 30,
  FourAndHalfMonths = 4.5 * 30,
  SevenMonths = 7 * 30,
  NineMonths = 9 * 30,
  ThirteenMonths = 13 * 30,
  EighteenMonths = 18 * 30,
  TwentyFiveMonths = 25 * 30,
}

export enum OilType {
  RegularOil = 'RegularOil',
  SyntheticOil = 'SyntheticOil',
}

export interface MaintenanceReminderOptionsType {
  title: string;
  value: UpdateDate | OilType | 'DoNotKnow';
  submitValue: string;
}

interface MaintenanceReminderStepType {
  title: string;
  icon: FunctionComponent<{ size?: number }>;
  options: Array<MaintenanceReminderOptionsType>;
  valueField: string;
  createAccountEvent: EventName;
  iconText?: React.ReactNode;
}

export interface MaintenanceReminderStepsMapType {
  MaintenanceOilChange: MaintenanceReminderStepType;
  MaintenanceOilType: MaintenanceReminderStepType;
  MaintenanceTireRotated: MaintenanceReminderStepType;
  MaintenanceBrakePad: MaintenanceReminderStepType;
}

export interface MaintenanceReminderConfigInputType {
  oilType?: string;
  oilChangeLastCompletedTime?: string;
  tireRotatedLastCompletedTime: string;
  brakePadsLastCompletedTime: string;
}

export const OIL_CHANGE_DATE_OPTIONS: MaintenanceReminderOptionsType[] = [
  {
    title: 'Within last month',
    value: UpdateDate.HalfMonth,
    submitValue: moment().subtract(UpdateDate.HalfMonth, 'day').toISOString(),
  },
  {
    title: '1-3 months ago',
    value: UpdateDate.TwoMonths,
    submitValue: moment().subtract(UpdateDate.TwoMonths, 'day').toISOString(),
  },
  {
    title: '3-6 months ago',
    value: UpdateDate.FourAndHalfMonths,
    submitValue: moment().subtract(UpdateDate.FourAndHalfMonths, 'day').toISOString(),
  },
  {
    title: 'More than 6 months ago',
    value: UpdateDate.SevenMonths,
    submitValue: moment().subtract(UpdateDate.SevenMonths, 'day').toISOString(),
  },
  {
    title: 'I don’t know',
    value: 'DoNotKnow',
    submitValue: moment().subtract(UpdateDate.FourAndHalfMonths, 'day').toISOString(),
  },
];

export const OIL_TYPE_OPTIONS: MaintenanceReminderOptionsType[] = [
  {
    title: 'Synthetic Oil',
    value: OilType.SyntheticOil,
    submitValue: OilType.SyntheticOil,
  },
  {
    title: 'Regular Oil',
    value: OilType.RegularOil,
    submitValue: OilType.SyntheticOil,
  },
  {
    title: 'I don’t know',
    value: 'DoNotKnow',
    submitValue: OilType.SyntheticOil,
  },
];

export const TIRE_ROTATED_DATE_OPTIONS: MaintenanceReminderOptionsType[] = [
  {
    title: 'Within last month',
    value: UpdateDate.HalfMonth,
    submitValue: moment().subtract(UpdateDate.HalfMonth, 'day').toISOString(),
  },
  {
    title: '3-6 months ago',
    value: UpdateDate.FourAndHalfMonths,
    submitValue: moment().subtract(UpdateDate.FourAndHalfMonths, 'day').toISOString(),
  },
  {
    title: '6-12 months ago',
    value: UpdateDate.NineMonths,
    submitValue: moment().subtract(UpdateDate.NineMonths, 'day').toISOString(),
  },
  {
    title: 'More than a year ago',
    value: UpdateDate.ThirteenMonths,
    submitValue: moment().subtract(UpdateDate.ThirteenMonths, 'day').toISOString(),
  },
  {
    title: 'I don’t know',
    value: 'DoNotKnow',
    submitValue: moment().subtract(UpdateDate.FourAndHalfMonths, 'day').toISOString(),
  },
];

export const BRAKE_PAD_DATE_OPTIONS: MaintenanceReminderOptionsType[] = [
  {
    title: 'Within last 6 months',
    value: UpdateDate.ThreeMonths,
    submitValue: moment().subtract(UpdateDate.ThreeMonths, 'day').toISOString(),
  },
  {
    title: '6-12 months ago',
    value: UpdateDate.NineMonths,
    submitValue: moment().subtract(UpdateDate.NineMonths, 'day').toISOString(),
  },
  {
    title: '1-2 years ago',
    value: UpdateDate.EighteenMonths,
    submitValue: moment().subtract(UpdateDate.EighteenMonths, 'day').toISOString(),
  },
  {
    title: 'More than 2 years ago',
    value: UpdateDate.TwentyFiveMonths,
    submitValue: moment().subtract(UpdateDate.TwentyFiveMonths, 'day').toISOString(),
  },
  {
    title: 'I don’t know',
    value: 'DoNotKnow',
    submitValue: moment().subtract(UpdateDate.TwentyFiveMonths, 'day').toISOString(),
  },
];

export const getMaintenanceReminderStepsConfig = (flowType?: FlowType): MaintenanceReminderStepsMapType => {
  const baseConfig: MaintenanceReminderStepsMapType = {
    MaintenanceOilChange: {
      title: 'When was the last oil change?',
      icon: MaintenanceOilChangeIcon,
      options: OIL_CHANGE_DATE_OPTIONS,
      valueField: 'oilChangeLastCompletedTime',
      createAccountEvent: 'CreateAccountAddMaintenanceReminderOilChangeLastCompletedTime',
    },
    MaintenanceOilType: {
      title: 'What type of oil do you use?',
      icon: MaintenanceOilChangeIcon,
      options: OIL_TYPE_OPTIONS,
      valueField: 'oilType',
      createAccountEvent: 'CreateAccountAddMaintenanceReminderOilType',
    },
    MaintenanceTireRotated: {
      title: 'When were the tires last rotated?',
      icon: MaintenanceTireRotatedIcon,
      options: TIRE_ROTATED_DATE_OPTIONS,
      valueField: 'tireRotatedLastCompletedTime',
      createAccountEvent: 'CreateAccountAddMaintenanceReminderTireRotatedLastCompletedTime',
    },
    MaintenanceBrakePad: {
      title: 'When were the brake pads last replaced?',
      icon: MaintenanceBrakePadIcon,
      options: BRAKE_PAD_DATE_OPTIONS,
      valueField: 'brakePadsLastCompletedTime',
      createAccountEvent: 'CreateAccountAddMaintenanceReminderBrakePadsLastCompletedTime',
    },
  };

  if (flowType === FlowType.RepairEstimate) {
    baseConfig.MaintenanceOilChange.iconText = (
      <Box>
        <Image alt="logos" height={100} width={100} src={CalenderImage} />
      </Box>
    );
    baseConfig.MaintenanceOilType.iconText = (
      <Box>
        <Image alt="logos" height={100} width={100} src={OilBarrelImage} />
      </Box>
    );
    baseConfig.MaintenanceTireRotated.iconText = (
      <Box>
        <Image alt="logos" height={100} width={100} src={CalenderImage} />
      </Box>
    );
    baseConfig.MaintenanceBrakePad.iconText = (
      <Box>
        <Image alt="logos" height={100} width={100} src={CalenderImage} />
      </Box>
    );
  }
  return baseConfig;
};
